import FilterConstants from 'filter-wizard/filter-constants';
import { FilterType, filters, SpecificationParameters, FilterStage } from '../filter';

export default {
  getStages(specificationParameters: SpecificationParameters): FilterStage[] {
    const lowPassStages = this.computeWideBandLowPass(specificationParameters);
    const highPassStages = this.computeWideBandHighPass(specificationParameters);

    let result = lowPassStages.concat(highPassStages);
    result = result.map((stage: FilterStage) => {
      stage.stageFz = 0;
      return stage;
    });
    return result;
  },

  getOrder(specificationParameters: SpecificationParameters): number {
    const fPassLowPass = this._getFPassLowPass(
      specificationParameters.passbandFrequency,
      specificationParameters.centerFrequency,
    );
    const fStopLowPass = this._getFStopLowPass(
      specificationParameters.stopbandFrequency,
      specificationParameters.centerFrequency,
    );

    const lowPass = filters[FilterConstants.filterType.lowPass as keyof FilterType];
    const lowPassOrder = lowPass.getOrder({
      filterResponseType: specificationParameters.filterResponseType,
      passbandFrequency: fPassLowPass,
      stopbandFrequency: fStopLowPass,
      passbandAttenuation: specificationParameters.passbandAttenuation,
      stopbandAttenuation: specificationParameters.stopbandAttenuation,
      centerFrequency: specificationParameters.centerFrequency,
      filterResponseValue: specificationParameters.filterResponseValue,
    });

    const fPassHighPass = this._getFPassHighPass(
      specificationParameters.passbandFrequency,
      specificationParameters.centerFrequency,
    );
    const fStopHighPass = this._getFStopHighPass(
      specificationParameters.stopbandFrequency,
      specificationParameters.centerFrequency,
    );

    const highPass = filters[FilterConstants.filterType.highPass as keyof FilterType];
    const highPassOrder = highPass.getOrder({
      filterResponseType: specificationParameters.filterResponseType,
      passbandFrequency: fPassHighPass,
      stopbandFrequency: fStopHighPass,
      passbandAttenuation: specificationParameters.passbandAttenuation,
      stopbandAttenuation: specificationParameters.stopbandAttenuation,
      centerFrequency: specificationParameters.centerFrequency,
      filterResponseValue: specificationParameters.filterResponseValue,
    });

    return lowPassOrder + highPassOrder;
  },

  getNumberOfStages(specificationParameters: SpecificationParameters): number {
    const fPassLowPass = this._getFPassLowPass(
      specificationParameters.passbandFrequency,
      specificationParameters.centerFrequency,
    );
    const fStopLowPass = this._getFStopLowPass(
      specificationParameters.stopbandFrequency,
      specificationParameters.centerFrequency,
    );

    const lowPass = filters[FilterConstants.filterType.lowPass as keyof FilterType];
    const lowPassOrder = lowPass.getNumberOfStages({
      filterResponseType: specificationParameters.filterResponseType,
      passbandFrequency: fPassLowPass,
      stopbandFrequency: fStopLowPass,
      passbandAttenuation: specificationParameters.passbandAttenuation,
      stopbandAttenuation: specificationParameters.stopbandAttenuation,
      centerFrequency: specificationParameters.centerFrequency,
      filterResponseValue: specificationParameters.filterResponseValue,
    });

    const fPassHighPass = this._getFPassHighPass(
      specificationParameters.passbandFrequency,
      specificationParameters.centerFrequency,
    );
    const fStopHighPass = this._getFStopHighPass(
      specificationParameters.stopbandFrequency,
      specificationParameters.centerFrequency,
    );

    const highPass = filters[FilterConstants.filterType.highPass as keyof FilterType];
    const highPassOrder = highPass.getNumberOfStages({
      filterResponseType: specificationParameters.filterResponseType,
      passbandFrequency: fPassHighPass,
      stopbandFrequency: fStopHighPass,
      passbandAttenuation: specificationParameters.passbandAttenuation,
      stopbandAttenuation: specificationParameters.stopbandAttenuation,
      centerFrequency: specificationParameters.centerFrequency,
      filterResponseValue: specificationParameters.filterResponseValue,
    });
    return lowPassOrder + highPassOrder;
  },

  computeWideBandLowPass(specificationParameters: SpecificationParameters): FilterStage[] {
    const fPass = this._getFPassLowPass(
      specificationParameters.passbandFrequency,
      specificationParameters.centerFrequency,
    );
    const fStop = this._getFStopLowPass(
      specificationParameters.stopbandFrequency,
      specificationParameters.centerFrequency,
    );

    const lowPass = filters[FilterConstants.filterType.lowPass as keyof FilterType];
    const result = lowPass.getStages({
      filterResponseType: specificationParameters.filterResponseType,
      passbandFrequency: fPass,
      stopbandFrequency: fStop,
      passbandAttenuation: specificationParameters.passbandAttenuation,
      stopbandAttenuation: specificationParameters.stopbandAttenuation,
      centerFrequency: specificationParameters.centerFrequency,
      filterResponseValue: specificationParameters.filterResponseValue,
    });

    return result.map((stage: FilterStage) => {
      stage.filter = lowPass;
      return stage;
    });
  },

  computeWideBandHighPass(specificationParameters: SpecificationParameters): FilterStage[] {
    const fPass = this._getFPassHighPass(
      specificationParameters.passbandFrequency,
      specificationParameters.centerFrequency,
    );
    const fStop = this._getFStopHighPass(
      specificationParameters.stopbandFrequency,
      specificationParameters.centerFrequency,
    );

    const highPass = filters[FilterConstants.filterType.highPass as keyof FilterType];
    const result = highPass.getStages({
      filterResponseType: specificationParameters.filterResponseType,
      passbandFrequency: fPass,
      stopbandFrequency: fStop,
      passbandAttenuation: specificationParameters.passbandAttenuation,
      stopbandAttenuation: specificationParameters.stopbandAttenuation,
      centerFrequency: specificationParameters.centerFrequency,
      filterResponseValue: specificationParameters.filterResponseValue,
    });

    return result.map((stage) => {
      stage.filter = highPass;
      return stage;
    });
  },

  // returns low pass passband frequency
  _getFPassLowPass(passbandFrequency: number, centerFrequency: number): number {
    return this._getFPass(passbandFrequency, centerFrequency, 1);
  },

  // returns high pass passband frequency
  _getFPassHighPass(passbandFrequency: number, centerFrequency: number): number {
    return this._getFPass(passbandFrequency, centerFrequency, -1);
  },

  // returns passband frequency
  _getFPass(passbandFrequency: number, centerFrequency: number, multiplier: number): number {
    return (
      (multiplier * passbandFrequency +
        Math.sqrt(passbandFrequency * passbandFrequency + 4 * centerFrequency * centerFrequency)) /
      2
    );
  },

  // returns low pass stopband frequency
  _getFStopLowPass(stopbandFrequency: number, centerFrequency: number): number {
    return this._getFStop(stopbandFrequency, centerFrequency, 1);
  },

  // returns high pass stopband frequency
  _getFStopHighPass(stopbandFrequency: number, centerFrequency: number): number {
    return this._getFStop(stopbandFrequency, centerFrequency, -1);
  },

  // returns stopband frequency
  _getFStop(stopbandFrequency: number, centerFrequency: number, multipler: number): number {
    return (
      (multipler * stopbandFrequency +
        Math.sqrt(stopbandFrequency * stopbandFrequency + 4 * centerFrequency * centerFrequency)) /
      2
    );
  },
};
